<!-- /live once every session of an event is over (LiveEvent.vue; Seán, 2026-11-03): thanks,
     and what comes next, below Earlier today (the sessions' cards). Previewed on the
     development servers by the test mode's Final State. Also /feedback's (views/Feedback.vue),
     as the page itself (isPlain: no card; Seán, 2026-11-03): the event and a line of
     introduction before the form, the thanks only once it's shared. -->

<template>
  <section class="endState" :class="{isPlain}" :aria-labelledby="hasHeading ? titleId : undefined">
    <!-- the form under one heading and one line, on /live and /feedback alike (Seán,
         2026-11-03): "Your feedback" on /live's card (/feedback's page title says it), then the
         muted "Help us improve future VIZBI events." Shared: the thanks ("Thanks for joining
         us!" and what comes next once the event is over; during it, just the thanks), and
         Update Feedback, which opens the form again with the answer given -->
    <h4 v-if="isAsking && !isPlain" :id="titleId">Your feedback</h4>
    <h4 v-else-if="!isAsking && isOver && !isPlain" :id="titleId">Thanks for joining us!</h4>
    <h4 v-else-if="!isAsking" :id="titleId">Thanks for your feedback!</h4>
    <template v-if="isAsking">
      <p class="intro" :class="{hasDeadline: isEditing && deadlineText}">
        Help us improve future VIZBI events.
      </p>
      <!-- changing an answer: until when (Seán, 2026-11-03), from the server's window -->
      <p v-if="isEditing && deadlineText" class="deadline">
        You can update your feedback until {{ deadlineText }}.
      </p>
      <LiveFeedback
        :key="isEditing ? 'update' : 'share'"
        :event-keys="isEditing ? [eventKey] : feedbackKeys"
        :is-preview
        :initial="isEditing ? givenAnswer : null"
        :is-update="isEditing"
        @shared="onShared"
        @cancel="isEditing = false" />
    </template>
    <template v-else>
      <template v-if="isOver">
        <p v-if="!isPlain" class="lead">
          Thanks for taking part in our first VIZBI <i>Online</i> event.
        </p>
        <ul>
          <li>
            <strong>The talks:</strong> the five-minute summary talks were recorded, and will be
            added to the <router-link to="/Videos">VIZBI videos</router-link>.
          </li>
          <li>
            <strong>The breakouts:</strong> their recordings won't be published; we'll use them
            to write summaries of what was discussed.
          </li>
          <li><strong>We'll email you</strong> as soon as these are available.</li>
        </ul>
      </template>
      <button v-if="isOpenToUpdates" type="button" class="update" @click="isEditing = true">
        Update Feedback
      </button>
    </template>
  </section>
</template>

<script setup>
import {computed, onMounted, ref, useId} from "vue";
import LiveFeedback from "@/components/LiveFeedback";
import {DateTime} from "luxon";
import history from "@/assets/history.json";
import useUserSafe from "@/composables/useUserSafe";
import {fetchFeedbackStatus, getFeedbackKey, getHasFeedback}
  from "@/composables/useEventFeedback";

const props = defineProps({
  eventName: {type: String, required: true},
  isPreview: {type: Boolean, default: false}, // the test mode's Final State: nothing recorded
  isPlain: {type: Boolean, default: false}, // /feedback: the page itself, not a card
  isOver: {type: Boolean, default: true} // the event has ended: what comes next, after thanks
});
const titleId = useId();
const {user} = useUserSafe();
const isLocallyShared = ref(false);
// the events this answer is for: this one, or both when the account took part in both and
// both are open and unanswered (one answer for both: FEEDBACK.md § Both events at once)
const eventKey = getFeedbackKey(props.eventName);
const openKeys = ref([]);
const feedbackKeys = computed(() => {
  return openKeys.value.length > 1 && openKeys.value.includes(eventKey) ? openKeys.value
    : [eventKey];
});
// the window's close (the server's: 30 days after the end), for the deadline and Update Feedback
const closesAt = ref("");
async function fetchOpenKeys() {
  try {
    const {events = [], closesAt: closing = ""} = await fetchFeedbackStatus(eventKey);
    closesAt.value = closing;
    if (props.isPreview) return;
    openKeys.value = events.filter(({state}) => state === "open").map(({name}) => name);
  } catch (error) {
    console.error(`LiveEndState: ${error}`); // this event alone, as before
  }
}
onMounted(fetchOpenKeys);

// a preview always asks first; a real visit, once given, the thanks and Update Feedback
const isShared = computed(() => {
  if (isLocallyShared.value) return true;
  return !props.isPreview && getHasFeedback(user.value, props.eventName);
});
const isEditing = ref(false);
// "3 December 2026", in the event's time zone (history.json's)
const deadlineText = computed(() => {
  if (!closesAt.value) return "";
  const zone = history.years[history.latestYear] || "UTC";
  return DateTime.fromISO(closesAt.value, {zone}).toFormat("d LLLL yyyy");
});
// past the deadline, the answer stays and can't be changed (the server refuses it too)
const isOpenToUpdates = computed(() => {
  return !closesAt.value || DateTime.now() < DateTime.fromISO(closesAt.value);
});
const isAsking = computed(() => !isShared.value || isEditing.value);
const hasHeading = computed(() => !(props.isPlain && isAsking.value));
// the answer given, for the form's update: the account's (a preview's, the one just given)
const previewAnswer = ref(null);
const givenAnswer = computed(() => {
  if (props.isPreview) return previewAnswer.value;
  return user.value?.publicMetadata?.events?.[eventKey]?.feedback || null;
});
function onShared(answer) {
  previewAnswer.value = answer;
  [isLocallyShared.value, isEditing.value] = [true, false];
}
</script>

<style scoped>
/* Update Feedback: Share Feedback's size and shape (LiveFeedback.vue), full width, but the
   outlined "you can press this" style of View Full Program (LiveEvent.vue .quietButton; Seán,
   2026-11-03): an optional action, not one to encourage */
button.update {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  margin: 32px 0 0;
  border: 1.5px solid color-mix(in srgb, white var(--second-text-lift), var(--brand-color));
  border-radius: 8px;
  box-shadow: none;
  background: none;
  color: color-mix(in srgb, white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color)));
  font: inherit;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
}
button.update:hover {
  background: color-mix(in srgb, var(--brand-color) 12%, transparent);
}
/* the deadline, while changing an answer: a quiet line above the form */
.intro.hasDeadline {
  margin-bottom: 8px; /* ▶ EDIT HERE: the introduction to the deadline, when it shows */
}
.deadline {
  margin: 0 0 32px; /* ▶ EDIT HERE: the deadline to the question */
  font-size: 0.9rem;
  color: color-mix(in srgb, var(--text-color) 60%, transparent);
}
button.update:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
/* a card as the sessions' (EventItem.vue), its text larger: the one thing on the page now */
.endState {
  /* the form's rhythm on an 8px grid (Seán, 2026-11-03): the heading 16px over its line, 32px
     to the question, 24px to the scores, 8px to their ends, 32px to the comments, 32px to
     the button; the card's padding the same at the top and the bottom */
  padding: 32px;
  border-radius: 12px;
  background-color: var(--background-color-subtle);
  text-align: left;
  margin-bottom: var(--live-section-gap, 24px); /* Live.vue's gap between blocks */
}
/* /feedback: the page's own background, the content as wide as the page allows */
.endState.isPlain {
  padding: 0;
  border-radius: 0;
  background: none;
}
/* /feedback's line of thanks: muted, a helper to the question below it */
.intro {
  margin: 0 0 32px; /* ▶ EDIT HERE: space between the introduction and the question */
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  color: color-mix(in srgb, var(--text-color) 60%, transparent);
}
h4 {
  margin: 0 0 16px; /* ▶ EDIT HERE: space between the title and the line below */
  line-height: 1.2; /* its line box no taller than the text needs: the gaps as set */
  font-size: 1.6rem;
  font-weight: 800;
}
.lead {
  margin: 0 0 20px; /* ▶ EDIT HERE: space between the lead and the list */
  font-size: 1.25rem;
}
ul {
  margin: 0;
  padding-left: 1.2em;
}
li {
  margin-bottom: 12px; /* ▶ EDIT HERE: space between the list's items */
  font-size: 1.1rem;
  line-height: 1.5;
}
li:last-child {
  margin-bottom: 0;
}
/* a phone: the card's padding a step less, the same at the top and the bottom */
@media (max-width: 600px) {
  .endState:not(.isPlain) {
    padding: 24px;
  }
}
</style>
